import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import RTPageLayout from '../common/RTPageLayout';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import Table from '../common/Table';
import PaymentReceiptPrint from './PaymentReceiptPrint';
import CancelPaymentModal from './CancelPaymentModal';
import { apiGet } from '../../api/apiClient';
import { toastError, toastSuccess } from '../common/toast';
import { formatDateToDDMMYYYY } from '../../utils/dateHelper';
import '../common/rtCommon.css';
import './ViewPaymentDetail.css';
import RTStatusPill, { formatStatusLabel } from '../common/statusPill';

const ViewPaymentDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { paymentId } = useParams();
  const [loading, setLoading] = useState(true);
  const [payment, setPayment] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [showPrintReceipt, setShowPrintReceipt] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  useEffect(() => {
    if (paymentId) {
      loadPaymentDetails();
    }
  }, [paymentId]);

  const loadPaymentDetails = async () => {
    try {
      setLoading(true);
      const response = await apiGet(`/payments/${paymentId}`);
      if (response && response.data) {
        setPayment(response.data);
        await loadInvoicesForPayment(response.data);
      }
    } catch (error) {
      console.error('Error loading payment details:', error);
      toastError('Failed to load payment details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadInvoicesForPayment = async (paymentData) => {
    try {
      if (paymentData.invoice_ids) {
        const invoiceIds = JSON.parse(paymentData.invoice_ids);
        const invoicePromises = invoiceIds.map(invoiceId => 
          apiGet(`/invoicing/invoices/${invoiceId}`)
        );
        
        const invoiceResponses = await Promise.all(invoicePromises);
        const invoiceData = invoiceResponses.map(response => response.data);
        setInvoices(invoiceData);
      }
    } catch (error) {
      console.error('Error loading invoices for payment:', error);
      toastError('Failed to load invoice details for payment.');
    }
  };

  const getInvoiceItemsColumns = () => [
    {
      Header: 'Type',
      accessor: 'invoice_item_type'
    },
    {
      Header: 'Description',
      accessor: 'description'
    },
    {
      Header: 'Quantity',
      accessor: 'quantity'
    },
    {
      Header: 'Amount',
      accessor: 'amount',
      Cell: ({ row }) => `₹${row.amount?.toFixed(2) || '0.00'}`
    }
  ];

  const getInvoiceItemsTableData = (invoice) => {
    if (!invoice || !invoice.invoice_items || !Array.isArray(invoice.invoice_items)) {
      return [];
    }
    return invoice.invoice_items;
  };

  const handlePrintReceipt = () => {
    setShowPrintReceipt(true);
  };

  const handleClosePrintReceipt = () => {
    setShowPrintReceipt(false);
  };

  const handleCancelPayment = () => {
    setShowCancelModal(true);
  };

  const handleCancelModalClose = () => {
    setShowCancelModal(false);
  };

  const handlePaymentCancelled = (updatedPayment) => {
    setPayment(updatedPayment);
    toastSuccess('Payment cancelled successfully');
  };

  const getInvoicesColumns = () => [
    {
      Header: 'Invoice #',
      accessor: 'invoice_number'
    },
    {
      Header: 'Date',
      accessor: 'invoice_date',
      Cell: ({ row }) => formatDateToDDMMYYYY(row.invoice_date)
    },
    {
      Header: 'Total Amount',
      accessor: 'total_amount',
      Cell: ({ row }) => `₹${row.total_amount?.toFixed(2) || '0.00'}`
    },
    {
      Header: 'Amount Paid',
      accessor: 'amount_paid',
      Cell: ({ row }) => `₹${row.amount_paid?.toFixed(2) || '0.00'}`
    },
    {
      Header: 'Amount Remaining',
      accessor: 'amount_remaining',
      Cell: ({ row }) => `₹${row.amount_remaining?.toFixed(2) || '0.00'}`
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: ({ row }) => {
        return (
          <RTStatusPill status={row.status || 'draft'}>
            {formatStatusLabel(row.status || 'draft')}
          </RTStatusPill>
        );
      }
    }
  ];

  const status = payment?.status?.toLowerCase();
  const headerActions = (
    <>
      <RTButton variant="secondary" onClick={() => navigate(-1)}>
        &#8592; Back
      </RTButton>
      {payment && status !== 'cancelled' ? (
        <>
          <RTButton variant="primary" onClick={handlePrintReceipt}>
            Print Receipt
          </RTButton>
          {status !== 'failed' ? (
            <RTButton
              variant="danger"
              permissionKey="payments.add_payment"
              onClick={handleCancelPayment}
            >
              Cancel Payment
            </RTButton>
          ) : null}
        </>
      ) : null}
    </>
  );

  let body;
  if (loading) {
    body = (
      <div className="payment-detail-loading">
        <RTSpinner message="Loading payment details..." fullScreen={false} />
      </div>
    );
  } else if (!payment) {
    body = <p>Payment not found.</p>;
  } else {
    body = (
      <div className="payment-detail-header">
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">Payment Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-600">Payment ID</label>
              <p className="text-sm font-mono">{payment.payment_id}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Payment Date</label>
              <p className="text-sm">{formatDateToDDMMYYYY(payment.payment_date)}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Amount</label>
              <p className="text-lg font-semibold" style={{ color: '#153C7F' }}>
                ₹{payment.amount?.toFixed(2) || '0.00'}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Payment Method</label>
              <p className="text-sm">
                {payment.payment_method
                  ? payment.payment_method.charAt(0).toUpperCase() +
                    payment.payment_method.slice(1).toLowerCase()
                  : 'N/A'}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Reference Number</label>
              <p className="text-sm">{payment.reference_number || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Status</label>
              <div className="mt-1">
                <RTStatusPill status={payment.status || 'pending'}>
                  {formatStatusLabel(payment.status || 'pending')}
                </RTStatusPill>
              </div>
            </div>
          </div>
          {payment.notes ? (
            <div className="mt-4">
              <label className="text-sm font-medium text-gray-600">Notes</label>
              <p>{payment.notes}</p>
            </div>
          ) : null}

          {status === 'cancelled' ? (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <h4 className="text-sm font-semibold text-red-800 mb-2">
                Cancellation Information
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {payment.cancellation_date ? (
                  <div>
                    <label className="text-sm font-medium text-red-700">
                      Cancellation Date
                    </label>
                    <p className="text-sm text-red-900">
                      {formatDateToDDMMYYYY(payment.cancellation_date)}
                    </p>
                  </div>
                ) : null}
              </div>
              {payment.cancellation_reason ? (
                <div className="mt-3">
                  <label className="text-sm font-medium text-red-700">
                    Cancellation Reason
                  </label>
                  <p className="text-sm text-red-900">{payment.cancellation_reason}</p>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold mb-4">
            Invoices Paid ({invoices.length})
          </h3>

          {invoices.length > 0 ? (
            <div className="space-y-6">
              {invoices.map((invoice) => (
                <div
                  key={invoice.invoice_id}
                  className="border-b pb-6 last:border-b-0 last:pb-0"
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h4 className="font-semibold">
                        Invoice #{invoice.invoice_number}
                      </h4>
                      <p className="text-sm text-gray-600">
                        Date: {formatDateToDDMMYYYY(invoice.invoice_date)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm">
                        Total: ₹{invoice.total_amount?.toFixed(2) || '0.00'}
                      </p>
                      <p className="text-sm" style={{ color: '#153C7F' }}>
                        Paid: ₹{invoice.amount_paid?.toFixed(2) || '0.00'}
                      </p>
                      {invoice.amount_remaining > 0 ? (
                        <p className="text-sm text-red-600">
                          Remaining: ₹{invoice.amount_remaining?.toFixed(2) || '0.00'}
                        </p>
                      ) : null}
                    </div>
                  </div>

                  <div className="mt-4">
                    <h5 className="font-medium mb-2">Invoice Items</h5>
                    <Table
                      data={getInvoiceItemsTableData(invoice)}
                      columns={getInvoiceItemsColumns()}
                      showSearch={false}
                      exportable={false}
                      title=""
                      noDataText="No invoice items found"
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">
              No invoices found for this payment.
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <RTPageLayout
      title="Payment Details"
      actions={headerActions}
      mainSection={true}
      activePath={location.pathname}
    >
      {body}
      {showPrintReceipt ? (
        <PaymentReceiptPrint
          paymentId={paymentId}
          onClose={handleClosePrintReceipt}
        />
      ) : null}
      <CancelPaymentModal
        isOpen={showCancelModal}
        onClose={handleCancelModalClose}
        payment={payment}
        onPaymentCancelled={handlePaymentCancelled}
      />
    </RTPageLayout>
  );
};

export default ViewPaymentDetail;

